/**
 * <alma-pay-button> — bouton "Payer en plusieurs fois avec Alma".
 *
 * Affiche un simple bouton ; au clic, ouvre la modale Alma (éligibilité +
 * échéancier) via le widget Alma.Widgets.Modal et son option clickableSelector.
 *
 * Le montant suit la variante sélectionnée : on écoute l'event `variant:change`
 * dispatché par <variant-selector> et on ré-instancie le widget (Alma démonte
 * automatiquement l'instance précédente avant de re-render).
 *
 * Config injectée en data-* par blocks/product-alma-button.liquid :
 *   data-merchant-id  ID marchand Alma
 *   data-mode         "TEST" | "LIVE"
 *   data-amount       montant en centimes (prix variante)
 *   data-monochrome   "true" | "false"
 *
 * Doc : https://github.com/alma/widgets/blob/master/documentation.md
 */
class AlmaPayButton extends HTMLElement {
  connectedCallback() {
    this.merchantId = this.dataset.merchantId;
    if (!this.merchantId) return;

    this.amount = parseInt(this.dataset.amount, 10) || 0;
    this.apiMode = this.dataset.mode === "TEST" ? "TEST" : "LIVE";
    this.monochrome = this.dataset.monochrome === "true";
    this.trigger = this.querySelector(".product_alma_button_cta");
    this.modalEl = this.querySelector(".product_alma_button_modal");
    if (!this.trigger || !this.modalEl) return;

    // Dispo initiale posée en Liquid (<button disabled> si la variante est en rupture).
    this.available = !this.trigger.disabled;

    // L'event `variant:change` bubble depuis <variant-selector> jusqu'à la
    // racine de section ; on écoute au plus près pour ne pas capter une autre PDP.
    this.section = this.closest("[data-section-id]") || document;
    this.section.addEventListener("variant:change", this.#onVariantChange);

    this.#whenAlmaReady().then(() => this.#render());
  }

  disconnectedCallback() {
    this.section?.removeEventListener("variant:change", this.#onVariantChange);
  }

  #onVariantChange = (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;

    // Out of stock : le bouton reste affiché mais grisé (.is-disabled) et le
    // <button disabled> neutralise le clic → le clickableSelector d'Alma ne se
    // déclenche pas. On ne masque plus le bouton.
    this.available = variant.available;
    this.classList.toggle("is-disabled", !variant.available);
    this.trigger.disabled = !variant.available;
    if (!variant.available) return;

    this.amount = variant.price; // Shopify : prix variante déjà en centimes
    this.#render();
  };

  /** (Ré)instancie le widget modale Alma avec le montant courant. */
  #render() {
    if (!this.available || !window.Alma || this.amount <= 0) return;

    this.widgets ||= window.Alma.Widgets.initialize(
      this.merchantId,
      this.apiMode === "TEST" ? window.Alma.ApiMode.TEST : window.Alma.ApiMode.LIVE,
    );

    // Ré-appeler add() avec un nouveau purchaseAmount démonte puis re-render
    // l'instance précédente (cf. doc Alma "Dynamic Updates").
    this.widgets.add(window.Alma.Widgets.Modal, {
      container: `#${this.modalEl.id}`,
      clickableSelector: `#${this.trigger.id}`,
      purchaseAmount: this.amount,
      monochrome: this.monochrome,
    });
  }

  /** Résout quand window.Alma est dispo (script CDN chargé en defer). */
  #whenAlmaReady() {
    return new Promise((resolve) => {
      if (window.Alma) return resolve();
      let tries = 0;
      const interval = setInterval(() => {
        if (window.Alma || ++tries > 200) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  }
}

if (!customElements.get("alma-pay-button")) {
  customElements.define("alma-pay-button", AlmaPayButton);
}
